import { create } from 'zustand';

import type { GameFlowState, InviteFriendToBattleParams } from '../types/gameFlow';

import {

  deriveMatchPhaseFromState,

  deriveMatchRole,

  MATCH_COUNTDOWN_SECONDS,

  shouldAbandonOnLeave,

} from '../types/matchmaking';

import { GameRoomType } from '../utils/enums';

import { axiosWithAuth } from '../utils/axiosWithAuth';

import { signalRService } from '../utils/signalr/signalRService';

import { showAlert } from '../utils/platform';

import { CreateGameRoomError, parseCreateGameRoomResponse } from './parseCreateGameRoomResponse';

import type {

  FriendMatchInvitePayload,

  MatchFoundPayload,

  OpponentLeftPayload,

  ReceiveGameRoomIDPayload,

  ReceiveInviteIDPayload,

  ReceiveMatchIDPayload,

} from '../utils/signalr/hubEvents';

import i18n from 'i18next';

import { attachGameFlowEffects } from './middleware/gameFlowEffects';

import {

  GUEST_WAIT_ROOM_SECONDS,

  incrementCountdownWait,

  resetCountdownWaitCounter,

  shouldShowCountdownTimeoutAlert,

} from './middleware/gameFlowTimerState';



type SetStateAction<T> = T | ((prev: T) => T);



const initialFlow: GameFlowState = {

  type: GameRoomType.SingleRandom,

  step: 'idle',

  isLoading: false,

};



export interface GameFlowStore extends GameFlowState {

  start: (flowState: GameFlowState) => Promise<void>;

  selectStock: (params: {

    symbol: string;

    startDate?: string;

    endDate?: string;

    interval?: number;

  }) => Promise<void>;

  selectMultiAssignStock: (symbol: string) => Promise<void>;

  setState: (updater: SetStateAction<GameFlowState>) => void;

  cancelMatch: () => void;

  cancelFriendInvite: () => void;

  inviteFriendToBattle: (params: InviteFriendToBattleParams) => void;

  friendBattleSelectAssignStock: (symbol: string) => void;

  acceptfriendBattleSelectAssignStock: (symbol: string) => Promise<void>;

  resetToIdle: () => void;

  clearAssignFieldsOnGameEnd: () => void;

  abandonMatchmaking: () => void;



  onMatchFound: (data: MatchFoundPayload) => void;

  onOpponentLeft: (data: OpponentLeftPayload) => void;

  onReceiveGameRoomID: (data: ReceiveGameRoomIDPayload) => void;

  onReceiveFriendMatchInvite: (data: FriendMatchInvitePayload) => void;

  onReceiveMatchID: (data: ReceiveMatchIDPayload) => void;

  onReceiveInviteID: (data: ReceiveInviteIDPayload) => void;

  onCancelFriendMatch: () => void;

  onFriendMatchRejected: () => void;

  onOpponentSceneReady: (roomId: string) => void;



  _joinMatchQueue: () => void;

  _sendRoomID: () => void;

  _handleCreateRoom: (flowState: GameFlowState) => Promise<void>;

  createRoomError: Record<string, unknown> | null;

  clearCreateRoomError: () => void;

  _tickCountdown: () => void;

  _clearFriendInviteFlow: () => void;

}



function usesAssignFieldsForCreate(type: GameRoomType): boolean {
  return type === GameRoomType.SingleAssign || type === GameRoomType.MultiAssign;
}

function clearAssignFields(): Partial<GameFlowState> {
  return {
    symbol: undefined,
    startDate: undefined,
    endDate: undefined,
    interval: undefined,
  };
}

async function fetchGameRoomId(flowState: GameFlowState): Promise<string> {

  const payload: Record<string, unknown> = {

    gameRoomType: Number(flowState.type),

    GameRoomType: Number(flowState.type),

  };

  if (usesAssignFieldsForCreate(flowState.type)) {
    if (flowState.symbol) payload.symbol = flowState.symbol;
    if (flowState.startDate) payload.startDate = flowState.startDate;
    if (flowState.endDate) payload.endDate = flowState.endDate;
    if (flowState.interval) payload.updateInterval = flowState.interval;
  }

  if (flowState.matchID != null) {

    payload.matchID = flowState.matchID;

    payload.MatchID = flowState.matchID;

  }



  const res = await axiosWithAuth('post', '/api/Game/Create', payload);

  return parseCreateGameRoomResponse(res);

}



function clearMatchFields(): Partial<GameFlowState> {

  return {

    matchID: undefined,

    opponentReady: false,

    countdown: undefined,

    roomId: undefined,

    inviteID: undefined,

    friendId: undefined,

    isFriendInvite: false,

    opponentID: undefined,

    createRoom: undefined,

    botType: undefined,

    opponentSceneReady: undefined,

    matchRole: undefined,

  };

}



export const useGameFlowStore = create<GameFlowStore>((set, get) => ({

  ...initialFlow,

  createRoomError: null,

  clearCreateRoomError: () => set({ createRoomError: null }),



  setState: (updater) => {

    if (typeof updater === 'function') {

      set((state) => ({ ...state, ...updater(state as GameFlowState) }));

    } else {

      set((state) => ({ ...state, ...updater }));

    }

  },



  resetToIdle: () =>

    set({

      ...initialFlow,

      ...clearMatchFields(),

      ...clearAssignFields(),

    }),



  clearAssignFieldsOnGameEnd: () => set(clearAssignFields()),



  abandonMatchmaking: () => {

    const state = get();

    if (!shouldAbandonOnLeave(state)) {

      return;

    }



    const phase = deriveMatchPhaseFromState(state);

    if (phase === 'inviting') {

      get().cancelFriendInvite();

      return;

    }

    if (phase === 'invited') {

      set({ step: 'rejectFriendInvite', isLoading: true });

      return;

    }

    if (phase === 'searching' || phase === 'countdown') {

      get().cancelMatch();

      return;

    }

    get().resetToIdle();

  },



  _clearFriendInviteFlow: () =>

    set({

      step: 'idle',

      ...clearMatchFields(),

      isLoading: false,

    }),



  start: async (flowState) => {

    if (flowState.type === GameRoomType.SingleRandom) {

      set({ type: GameRoomType.SingleRandom, step: 'idle', ...clearAssignFields() });

      await get()._handleCreateRoom({

        type: GameRoomType.SingleRandom,

        step: 'idle',

      });

      return;

    }

    if (flowState.type === GameRoomType.SingleAssign) {

      set({

        type: GameRoomType.SingleAssign,

        step: 'stockSelect',

        isLoading: false,

        ...clearAssignFields(),

      });

      return;

    }

    if (flowState.type === GameRoomType.MultiRandom) {

      set({

        type: GameRoomType.MultiRandom,

        step: 'matching',

        isLoading: false,

        ...clearMatchFields(),

        ...clearAssignFields(),

      });

      return;

    }

    if (flowState.type === GameRoomType.MultiAssign) {

      set({

        type: GameRoomType.MultiAssign,

        step: 'multiAssignStockSelect',

        isLoading: false,

        ...clearMatchFields(),

        ...clearAssignFields(),

      });

    }

  },



  selectStock: async ({ symbol, startDate, endDate, interval }) => {

    set({ isLoading: true });

    await get()._handleCreateRoom({

      type: GameRoomType.SingleAssign,

      step: 'stockSelect',

      symbol,

      startDate,

      endDate,

      interval,

    });

  },



  selectMultiAssignStock: async (symbol) => {

    set({

      type: GameRoomType.MultiAssign,

      symbol,

      step: 'matching',

      matchID: undefined,

      opponentReady: false,

      countdown: undefined,

      roomId: undefined,

      matchRole: undefined,

      isLoading: false,

    });

  },



  cancelMatch: () => {

    const { matchID } = get();

    if (matchID != null) {

      signalRService.invoke('LeftMatchQueue', matchID).catch(() => {});

    }

    resetCountdownWaitCounter();

    set({

      step: 'idle',

      ...clearMatchFields(),

      ...clearAssignFields(),

      isLoading: false,

    });

  },



  cancelFriendInvite: () => {

    const { inviteID } = get();

    if (inviteID != null) {

      signalRService.invoke('CancelFriendMatch', inviteID).catch(() => {});

    }

    get()._clearFriendInviteFlow();

  },



  inviteFriendToBattle: ({ friendId, type, symbol }) => {

    set({

      type,

      friendId,

      symbol,

      isFriendInvite: true,

      step: 'inviteFriendToMatch',

      isLoading: true,

      matchID: undefined,

      inviteID: undefined,

      matchRole: undefined,

    });

  },



  friendBattleSelectAssignStock: (symbol) =>

    set({ step: 'inviteFriendToMatch', symbol, isLoading: true }),



  acceptfriendBattleSelectAssignStock: async (symbol) => {

    const { inviteID } = get();

    await signalRService.invoke('AcceptFriendMatch', inviteID, symbol);

    set({ step: 'matching', symbol, isLoading: true, matchID: undefined, matchRole: undefined });

  },



  _joinMatchQueue: () => {

    const { type, symbol } = get();

    const queueSymbol =

      type === GameRoomType.MultiAssign ? symbol || undefined : undefined;

    signalRService.invoke('JoinMatchQueue', type, queueSymbol).catch(() => {});

  },



  _sendRoomID: async () => {

    const { roomId, step } = get();

    if (!roomId || step !== 'sendRoomID') return;

    const gameRoomId = Number(roomId);

    if (!Number.isFinite(gameRoomId)) return;

    try {

      await signalRService.invoke('SendGameRoomID', gameRoomId);

    } catch {

      return;

    }

    if (get().step !== 'sendRoomID') return;

    set({ step: 'playing', isLoading: true });

  },



  _handleCreateRoom: async (flowState) => {

    try {

      const gameRoomId = await fetchGameRoomId(flowState);

      if (

        flowState.type === GameRoomType.MultiRandom ||

        flowState.type === GameRoomType.MultiAssign

      ) {

        set({ ...flowState, step: 'sendRoomID', roomId: gameRoomId, isLoading: true });

      } else {

        set({ ...flowState, step: 'playing', roomId: gameRoomId, isLoading: true });

      }

    } catch (err) {

      const createRoomError =
        err instanceof CreateGameRoomError ? err.apiBody : null;

      set((prev) => ({

        step: prev.type === GameRoomType.SingleAssign ? 'stockSelect' : 'idle',

        isLoading: false,

        createRoomError,

      }));

    }

  },



  _tickCountdown: () => {

    const state = get();

    if (state.step !== 'countdown') return;

    if (state.roomId) return;



    const currentCount = state.countdown ?? 0;



    if (currentCount > 0) {

      set({ countdown: currentCount - 1 });

      return;

    }



    const role =

      state.matchRole ?? deriveMatchRole(state.opponentID, state.createRoom);



    if (role === 'host' || role === 'solo_bot') {

      if (state.matchID == null) return;

      set({ createRoom: false, countdown: 0, step: 'sendRoomID' });

      const createFlow: GameFlowState = {

        type: state.type,

        step: 'sendRoomID',

        matchID: state.matchID,

        ...(state.type === GameRoomType.MultiAssign && state.symbol

          ? { symbol: state.symbol }

          : clearAssignFields()),

      };

      void get()._handleCreateRoom(createFlow);

      return;

    }



    if (role === 'guest') {

      if (incrementCountdownWait() > GUEST_WAIT_ROOM_SECONDS) {

        if (state.matchID) {

          signalRService.invoke('LeftMatchQueue', state.matchID).catch(() => {});

        }

        if (shouldShowCountdownTimeoutAlert()) {

          setTimeout(() => {

            const latest = get();

            if (latest.roomId || latest.step === 'playing' || latest.step === 'matching') return;

            showAlert(

              i18n.t('common.errorTitle'),

              i18n.t('matchingModal.timeout'),

            );

          }, 100);

        }

        set({

          step: 'idle',

          ...clearMatchFields(),

          isLoading: false,

        });

      }

      return;

    }



    if (incrementCountdownWait() > GUEST_WAIT_ROOM_SECONDS) {

      if (state.matchID) {

        signalRService.invoke('LeftMatchQueue', state.matchID).catch(() => {});

      }

      if (shouldShowCountdownTimeoutAlert()) {

        setTimeout(() => {

          const latest = get();

          if (latest.roomId || latest.step === 'playing' || latest.step === 'matching') return;

          showAlert(

            i18n.t('common.errorTitle'),

            i18n.t('matchingModal.timeout'),

          );

        }, 100);

      }

      set({

        step: 'idle',

        ...clearMatchFields(),

        isLoading: false,

      });

    }

  },



  onMatchFound: (data) => {

    const state = get();

    if (state.step === 'countdown' && state.matchID === data.matchID) return;



    const matchRole = deriveMatchRole(data.opponentID, data.createRoom);

    set({

      matchID: data.matchID,

      opponentReady: true,

      step: 'countdown',

      countdown:

        state.step === 'countdown' ? (state.countdown ?? MATCH_COUNTDOWN_SECONDS) : MATCH_COUNTDOWN_SECONDS,

      opponentID: data.opponentID,

      createRoom: data.createRoom,

      botType: data.botType,

      matchRole,

    });

  },



  onOpponentLeft: (data) =>

    set({

      step: 'matching',

      matchID: data.matchID,

      opponentReady: false,

      countdown: undefined,

      roomId: undefined,

      createRoom: undefined,

      opponentID: undefined,

      botType: undefined,

      matchRole: undefined,

    }),



  onReceiveGameRoomID: (data) =>

    set({ step: 'playing', roomId: data.gameRoomID, isLoading: true }),



  onReceiveFriendMatchInvite: (data) =>

    set({

      type: data.gameRoomType,

      step: 'answerFriendResponse',

      friendId: data.fromUserId,

      inviteID: data.inviteID,

      isFriendInvite: true,

    }),



  onReceiveMatchID: (data) =>

    set({ matchID: data.matchID, step: 'matching' }),



  onReceiveInviteID: (data) =>

    set({ inviteID: data.inviteID, step: 'waitingFriendResponse' }),



  onCancelFriendMatch: () => set({ step: 'receiveFriendInviteCancel', isLoading: true }),



  onFriendMatchRejected: () => set({ step: 'receiveFriendReject', isLoading: true }),



  onOpponentSceneReady: (_roomId) => {

    set({ opponentSceneReady: true });

  },

}));



attachGameFlowEffects(useGameFlowStore);


